import { useEffect, useState, useMemo, useRef } from 'react';
import './CategoryRecipeList.css';
import RecipeCard from './RecipeCard';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import { mergeRecipeTitles } from '../utils/fetchTranslatedRecipes';

const LS_KEY = 'solt_pantry_swipe_saved';

function CategoryRecipeList({ category, onSelectRecipe, onBack, favouritesOnly, allRecipesOnly, visitorPicksOnly }) {
  const { language } = useLanguage();
  const t = translations[language];
  const { user, isAdmin, isMember } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Visitor saved IDs (localStorage) — only used when not logged in
  const [visitorSavedIds, setVisitorSavedIds] = useState(() => {
    if (typeof window === 'undefined') return new Set();
    return new Set(JSON.parse(localStorage.getItem(LS_KEY) || '[]'));
  });

  // Filter / sort state
  const [sort, setSort] = useState('newest');       // 'az' | 'za' | 'quickest' | 'newest'
  const [diffFilter] = useState(''); // '' | 'easy' | 'medium' | 'hard'
  const [favsOnly, setFavsOnly] = useState(false);
  const [maxTime, setMaxTime] = useState(null);        // null | 30 | 60
  const [dietFilters, setDietFilters] = useState(new Set()); // 'vegetarian' | 'low-carb'

  function toggleMaxTime(val) {
    setMaxTime(prev => prev === val ? null : val);
  }
  function toggleDiet(key) {
    setDietFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }
  function clearAllFilters() {
    setMaxTime(null);
    setDietFilters(new Set());
    setFavsOnly(false);
  }
  const hasActiveFilters = maxTime !== null || dietFilters.size > 0 || favsOnly;

  useEffect(() => {
    async function fetchRecipes() {
      let data = [];

      if (visitorPicksOnly) {
        const stored = JSON.parse(localStorage.getItem(LS_KEY) || '[]');
        if (stored.length > 0) {
          const { data: recs } = await supabase.from('recipes').select('*').in('id', stored);
          data = recs || [];
        }
      } else if (allRecipesOnly) {
        const { data: recs, error } = await supabase
          .from('recipes').select('*')
          .order('created_at', { ascending: false });
        if (error) console.error('Error fetching all recipes:', error);
        data = recs || [];
        if (isMember && user && data.length > 0) {
          const ids = data.map(r => r.id);
          const { data: favRows } = await supabase
            .from('user_favourites').select('recipe_id')
            .eq('user_id', user.id).in('recipe_id', ids);
          const favSet = new Set((favRows || []).map(r => r.recipe_id));
          data = data.map(r => ({ ...r, is_favourite: favSet.has(r.id) }));
        }
      } else if (favouritesOnly) {
        if (isMember && user) {
          const { data: favRows } = await supabase
            .from('user_favourites')
            .select('recipe_id')
            .eq('user_id', user.id);
          const ids = (favRows || []).map(r => r.recipe_id);
          if (ids.length > 0) {
            const { data: recs } = await supabase.from('recipes').select('*').in('id', ids);
            data = (recs || []).map(r => ({ ...r, is_favourite: true }));
          }
        } else {
          const { data: recs } = await supabase.from('recipes').select('*').eq('is_favourite', true);
          data = recs || [];
        }
      } else {
        const { data: recs, error } = await supabase
          .from('recipes').select('*')
          .eq('category', category.name)
          .order('created_at', { ascending: true });
        if (error) console.error('Error fetching recipes:', error);
        data = recs || [];

        if (isMember && user && data.length > 0) {
          const ids = data.map(r => r.id);
          const { data: favRows } = await supabase
            .from('user_favourites')
            .select('recipe_id')
            .eq('user_id', user.id)
            .in('recipe_id', ids);
          const favSet = new Set((favRows || []).map(r => r.recipe_id));
          data = data.map(r => ({ ...r, is_favourite: favSet.has(r.id) }));
        }
      }

      const merged = await mergeRecipeTitles(data, language);
      setRecipes(merged);
      setLoading(false);
    }
    fetchRecipes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.name, language, favouritesOnly, allRecipesOnly, visitorPicksOnly, isMember, user?.id]);

  function handleToggleFavourite(id, newValue) {
    if (!isAdmin && !isMember) {
      // Normalise id to number to avoid string/number type mismatch
      const numId = Number(id);
      const current = JSON.parse(localStorage.getItem(LS_KEY) || '[]').map(Number);
      const nextArr = newValue
        ? [...new Set([...current, numId])]
        : current.filter(x => x !== numId);
      localStorage.setItem(LS_KEY, JSON.stringify(nextArr));
      setVisitorSavedIds(new Set(nextArr));
      // Remove from list immediately when un-hearting in My Picks view
      if (visitorPicksOnly && !newValue) {
        setRecipes(prev => prev.filter(r => Number(r.id) !== numId));
      }
      // Notify homepage to refresh its count (localStorage already updated)
      window.dispatchEvent(new CustomEvent('visitorPicksChanged'));
      return;
    }
    setRecipes(prev => prev.map(r => r.id === id ? { ...r, is_favourite: newValue } : r));
    if (isMember && user) {
      if (newValue) {
        supabase.from('user_favourites').insert({ user_id: user.id, recipe_id: id });
      } else {
        supabase.from('user_favourites').delete().eq('user_id', user.id).eq('recipe_id', id);
      }
    } else {
      supabase.from('recipes').update({ is_favourite: newValue }).eq('id', id);
    }
  }

  // Apply filters + sort client-side
  const displayed = useMemo(() => {
    let list = [...recipes];
    if (favsOnly) list = list.filter(r => r.is_favourite);
    if (diffFilter) list = list.filter(r => r.difficulty === diffFilter);
    if (maxTime === 30) list = list.filter(r => (r.prep_time_mins||0)+(r.cook_time_mins||0) < 30);
    if (maxTime === 60) list = list.filter(r => (r.prep_time_mins||0)+(r.cook_time_mins||0) >= 60);
    if (dietFilters.size > 0) {
      list = list.filter(r => {
        const tags = (r.dietary_tags || []).map(t => t.toLowerCase().replace(/\s+/g, '-'));
        if (dietFilters.has('vegetarian') && !tags.some(t => t.includes('vegetarian'))) return false;
        if (dietFilters.has('low-carb') && !tags.some(t => t.includes('low') && t.includes('carb'))) return false;
        return true;
      });
    }
    switch (sort) {
      case 'az':       list.sort((a, b) => a.title.localeCompare(b.title)); break;
      case 'za':       list.sort((a, b) => b.title.localeCompare(a.title)); break;
      case 'quickest': list.sort((a, b) => ((a.prep_time_mins||0)+(a.cook_time_mins||0)) - ((b.prep_time_mins||0)+(b.cook_time_mins||0))); break;
      default:         list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); break;
    }
    return list;
  }, [recipes, favsOnly, diffFilter, maxTime, dietFilters, sort]);

  const gridRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    const grid = gridRef.current;
    if (!grid) return;
    const items = grid.querySelectorAll('.animate-in');
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.06 }
    );
    items.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [loading, displayed]);

  const title = visitorPicksOnly
    ? t.myPicks
    : allRecipesOnly
      ? t.allRecipes
      : favouritesOnly
        ? t.favourites
        : (t.categories[category?.name] || category?.name);

  return (
    <main className="category-recipe-list">
      <div className="category-recipe-list__header">
        <button className="category-recipe-list__back" onClick={onBack} title={t.allCategories}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
        </button>
        <h2 className="category-recipe-list__title">{title}</h2>
      </div>

      {/* Filter / sort bar */}
      <div className="recipe-filter-bar">
        <div className="recipe-filter-bar__group">
          <span className="recipe-filter-bar__label">{t.sortLabel}</span>
          {[
            { value: 'newest', label: t.sortNewest },
            { value: 'az',     label: t.sortAZ },
            { value: 'za',     label: t.sortZA },
            { value: 'quickest', label: t.sortQuickest },
          ].map(opt => (
            <button
              key={opt.value}
              className={`recipe-filter-bar__btn${sort === opt.value ? ' recipe-filter-bar__btn--active' : ''}`}
              onClick={() => setSort(opt.value)}
            >{opt.label}</button>
          ))}
        </div>

        <div className="recipe-filter-bar__group">
          <button
            className={`recipe-filter-bar__btn${maxTime === 30 ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => toggleMaxTime(30)}
          >{t.filterUnder30}</button>
          <button
            className={`recipe-filter-bar__btn${maxTime === 60 ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => toggleMaxTime(60)}
          >{t.filter60Plus}</button>
          <button
            className={`recipe-filter-bar__btn${dietFilters.has('vegetarian') ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => toggleDiet('vegetarian')}
          >{t.filterVegetarian}</button>
          <button
            className={`recipe-filter-bar__btn${dietFilters.has('low-carb') ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => toggleDiet('low-carb')}
          >{t.filterLowCarb}</button>
        </div>

        {(isAdmin || isMember) && !favouritesOnly && !allRecipesOnly && (
          <button
            className={`recipe-filter-bar__btn recipe-filter-bar__btn--heart${favsOnly ? ' recipe-filter-bar__btn--active' : ''}`}
            onClick={() => setFavsOnly(v => !v)}
          >
            {favsOnly ? '♥' : '♡'} {t.favouritesOnly}
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#999', fontStyle: 'italic' }}>{t.loading}</p>
      ) : displayed.length === 0 ? (
        <div className="category-recipe-list__empty">
          <p>{hasActiveFilters ? t.noRecipesMatchFilters : visitorPicksOnly ? t.noMyPicks : favouritesOnly ? t.noFavourites : t.noRecipesInCategory}</p>
          {hasActiveFilters && (
            <button className="recipe-filter-bar__btn" onClick={clearAllFilters}>{t.clearFilters}</button>
          )}
        </div>
      ) : (
        <div className="category-recipe-list__grid" ref={gridRef}>
          {displayed.map((recipe, index) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              onClick={() => onSelectRecipe(recipe.id)}
              onToggleFavourite={handleToggleFavourite}
              isAdmin={isAdmin || isMember}
              visitorLiked={!isAdmin && !isMember ? visitorSavedIds.has(recipe.id) : undefined}
              animateDelay={`${index * 0.07}s`}
              showDraft={isAdmin}
            />
          ))}
        </div>
      )}
    </main>
  );
}

export default CategoryRecipeList;
